import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import './RespondToSuggestion.css';

const API = process.env.REACT_APP_API_BASE_URL;

// Public, no-login page reached from the unsubscribe link auto-appended to
// every outbound email (see backend/services/emailSendingCore.js). A
// deliberate checkbox + Save step rather than an automatic one-click
// unsubscribe, so a link opened by accident (or scanned by an email
// client's link-preview bot) can't silently change anyone's preference.
const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [firstName, setFirstName] = useState('');
  const [unsubscribed, setUnsubscribed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!token) { setError('This link looks incomplete — please use the link from your email directly.'); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/unsubscribe/status?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) { setError(data.error || 'This link is invalid.'); return; }
        setFirstName(data.firstName || '');
        setUnsubscribed(!!data.unsubscribed);
      } catch (err) {
        if (!cancelled) setError('Something went wrong loading this page — please try again shortly.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/unsubscribe/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, unsubscribed }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not save your preference.'); return; }
      setSaved(true);
    } catch (err) {
      setError('Could not save your preference — please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="respond-page">
        <div className="respond-page-inner">
          <div className="respond-card respond-card--message">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="respond-page">
        <div className="respond-page-inner">
          <div className="respond-card respond-card--message">
            <p>Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="respond-page">
      <div className="respond-page-inner">
        <h1 className="respond-heading">Email preferences{firstName ? ` for ${firstName}` : ''}</h1>

        <div className="respond-card" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              className="unsubscribe-checkbox"
              checked={unsubscribed}
              onChange={(e) => { setUnsubscribed(e.target.checked); setSaved(false); }}
            />
            <span>Unsubscribe me from all Interconnector emails</span>
          </label>

          <div className="respond-actions" style={{ marginTop: '1.25rem' }}>
            <button type="button" className="button-fancy" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save preference'}
            </button>
          </div>
          {saved && <div className="respond-done-note" style={{ marginTop: '0.75rem' }}>Saved — your preference has been updated.</div>}
        </div>

        <div className="respond-intro-sub" style={{ marginTop: '1.5rem' }}>
          This only controls emails — it doesn't delete your account or profile. To delete your account entirely, please <Link to="/login">log in</Link> and do that from your account settings.
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
